import { useActionSignatureContext } from "app/features/actionSignature/providers/ActionSignatureProvider";
import { useResidentContext } from "app/features/residents/provider/ResidentProvider";
import {
  useCreateMedicationReportMutation,
  useShowMedicationQuery,
} from "app/services/api/medicationsApi";
import { AdministerMedicationRequest } from "app/services/api/types/medications/requests/AdministerMedicationRequest";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { MedicationAdministrationStatusEnum } from "../../../services/api/types/medications/enums/MedicationAdministrationStatusEnum";

interface Props {
  onAdministerSuccess?: () => void;
  selfAdminister?: boolean;
  skipped?: boolean;
}

export function useMedicationAdministration(
  medicationId?: number,
  { onAdministerSuccess, selfAdminister = false, skipped = false }: Props = {},
) {
  const { residentId, resident } = useResidentContext();
  const { showActionSignatureModal } = useActionSignatureContext();
  const { showNewToast, showErrorToast } = useDisplayToast();
  const [triggerAdminister] = useCreateMedicationReportMutation();

  const {
    data: medicationItem,
    isFetching: isMedicationFetching,
    isLoading: isMedicationLoading,
  } = useShowMedicationQuery(
    { medication: medicationId as number },
    { skip: medicationId === undefined },
  );

  const status: MedicationAdministrationStatusEnum = skipped
    ? MedicationAdministrationStatusEnum.SKIPPED
    : selfAdminister
      ? MedicationAdministrationStatusEnum.SELF_ADMINISTERED
      : MedicationAdministrationStatusEnum.ADMINISTERED;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<AdministerMedicationRequest>({
    defaultValues: {
      resident_id: residentId || undefined,
      medication_id: medicationId || undefined,
      dosage: medicationItem?.dosage || "",
      other_information: medicationItem?.notes || "",
      status,
      skip_reason: "",
    },
  });

  const administerMedication = handleSubmit(
    async (medicationData: AdministerMedicationRequest) => {
      try {
        const resp = await showActionSignatureModal({
          title: "Administer Medication",
          description:
            "Please enter your password to administer this medication",
        });
        const transformedData = {
          ...medicationData,
          password: resp.password,
        };

        await triggerAdminister({
          body: transformedData,
        }).unwrap();

        showNewToast({
          title: "Medication Administered",
          message: "Successfully administered medication",
          type: "success",
        });
        if (onAdministerSuccess) onAdministerSuccess();
      } catch (e) {
        showErrorToast(e);
      }
    },
  );

  const residentInfo = useMemo(() => {
    return {
      name: `${resident?.first_name || ""} ${resident?.last_name || ""}`.trim(),
      nickname: resident?.known_as || null,
      roomNumber: resident?.resident_id || "None specified",
      bedNumber: resident?.bed?.bed_number || "No bed specified",
      admittedDate: resident?.admission_start_date || "No date specified",
      status: resident?.status,
      profile: resident?.profile_picture?.url || null,
    };
  }, [resident]);

  return {
    medicationItem,
    isMedicationFetching,
    isMedicationLoading,
    isSubmitting,
    residentInfo,
    control,
    administerMedication,
  };
}
